import PaymentFields from "@/components/PaymentFields";
import FiscalOrderDialog, { fiscalError, isFiscalAdmin } from "@/components/FiscalOrderDialog";
import DeliveryFields from "@/components/DeliveryFields";
import { cartLines, cents, moneyText, newestOrder, cpfError } from "@/lib/orderPayload";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ClipboardList, Plus, Trash2, DollarSign,
  ReceiptText, Pencil, Truck, Printer, Clock, X, AlertTriangle
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import api, { updateOrder } from "@/lib/api";
import { API_BASE } from "@/lib/backend";
import { createOrderSubmission } from "@/lib/orderSubmission";
import { printReceipt } from "@/lib/printReceipt";

const STATUS_MAP = {
  confirmado: { label: "Confirmado", cls: "bg-green-900/50 text-green-100 border-transparent" },
  preparando: { label: "Preparando", cls: "bg-blue-900/50 text-blue-100 border-transparent" },
  Retirada: { label: "Pronto para retirada", cls: "bg-yellow-900/50 text-yellow-100 border-transparent" },
  entregue: { label: "Entregue", cls: "bg-emerald-900/50 text-emerald-100 border-transparent" },
  cancelado: { label: "Cancelado", cls: "bg-red-900/50 text-red-100 border-transparent" },
};

function fmtBRL(v) {
  const num = parseFloat(v) || 0;
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
}

const TIPO_LABELS = { pizza_familia: "Pizza Familia", dobrada: "Dobrada", lanche: "Lanche", bebida: "Bebida" };

// ============= ORDER DIALOG (CREATE + EDIT) =============
export function OrderDialog({ open, onOpenChange, onSaved, editingOrder }) {
  const [submission] = useState(() => {
    let usuario;
    try { usuario = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null")?.id; } catch { /* bloquear abaixo */ }
    return createOrderSubmission(sessionStorage, { backend: API_BASE, canal: "painel", usuario: usuario || "sem-operador", compra: editingOrder?.pedido_uid || editingOrder?.id || "nova" });
  });
  const restored = submission.record.rascunho;
  const [submissionState, setSubmissionState] = useState(submission.record);
  const [storageError, setStorageError] = useState(submission.error);
  const [ready, setReady] = useState(false);
  const saveGuard = useRef(false);
  const [menuItems, setMenuItems] = useState([]);
  const [form, setForm] = useState({
    nomeCliente: "", telefoneCliente: "", descPedido: "",
    enderecoCliente: "", statusPedido: "confirmado", Valor_Total: "",
    pagamento: "", taxa_entrega: "0", Impresso: "false",
    finalizar_pedido: "true", cardapio_enviado: "true", pix: ""
  });
  const[saving, setSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [payments, setPayments] = useState([{ metodo: "pix_estatico", valor: "", confirmado: false }]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [editConflict, setEditConflict] = useState(false);

  function loadOrder(data) {
    setForm(data);
    setSelectedItems((data.itens || []).map(item => ({
      id: item.segundo_sabor_id ? `half-${item.produto_id}-${item.segundo_sabor_id}` : item.produto_id,
      nome_item: item.nome, valor: item.valor_unitario, qty: item.quantidade, observacoes: item.observacoes,
    })));
    setPayments(data.pagamentos || [{ metodo: "pix_estatico", valor: data.Valor_Total || "", confirmado: false }]);
  }

  useEffect(() => {
    if (!open) return;
    setDetailsError(false);
    let cancelled = false;
    api.get("/menu-items").then(r => { if (!cancelled) setMenuItems(r.data.filter(i => i.disponivel)); }).catch(() => {});
    if (restored.form) {
      setForm(restored.form); setSelectedItems(restored.cart); setPayments(restored.payments);
      setReady(true); setDetailsLoading(false);
      return () => { cancelled = true; };
    }
    if (!editingOrder) {
      setDetailsLoading(false);
      setPayments([{ metodo: "pix_estatico", valor: "", confirmado: false }]);
      setReady(true);
      return () => { cancelled = true; };
    }
    setDetailsLoading(true);
    api.get(`/orders/${editingOrder.id}/data`).then(({ data }) => {
      if (cancelled) return;
      loadOrder(data);
      setReady(true);
    }).catch(() => { if (!cancelled) setDetailsError(true); })
      .finally(() => { if (!cancelled) setDetailsLoading(false); });
    return () => { cancelled = true; };
    // O diálogo é remontado por compra/operador; reabrir não busca outra versão sobre o formulário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      const { html_comanda, html, items_html, ...savedForm } = form;
      submission.saveDraft({ cart: selectedItems.map(({ id, qty, nome_item, valor, tipo, tamanho, observacoes }) => ({ id, qty, nome_item, valor, tipo, tamanho, observacoes })), form: savedForm, payments,
        editingOrder: editingOrder ? { id: editingOrder.id, pedido_uid: editingOrder.pedido_uid } : null });
      setStorageError(submission.error);
    } catch (err) { setStorageError(err.message); }
  }, [ready, submission, form, selectedItems, payments, editingOrder]);

  const menuByTipo = menuItems.reduce((acc, item) => {
    if (!acc[item.tipo]) acc[item.tipo] =[];
    acc[item.tipo].push(item);
    return acc;
  }, {});


  const addSelectedItem = (item) => {
    setSelectedItems(prev => {
      const existing = prev.find(s => s.id === item.id);
      if (existing) return prev.map(s => s.id === item.id ? { ...s, qty: s.qty + 1 } : s);
      return[...prev, { id: item.id, nome_item: item.nome_item, tipo: item.tipo, valor: parseFloat(item.valor) || 0, qty: 1 }];
    });
  };

  const removeSelectedItem = (id) => setSelectedItems(prev => prev.filter(s => s.id !== id));
  const updateQty = (id, qty) => {
    if (qty < 1) return removeSelectedItem(id);
    setSelectedItems(prev => prev.map(s => s.id === id ? { ...s, qty } : s));
  };

  useEffect(() => {
    if (!ready || submission.locked) return;
    if (selectedItems.length > 0) {
      const lines = selectedItems.map(s => `${s.qty} - ${s.nome_item}${s.tipo ? ` (${TIPO_LABELS[s.tipo] || s.tipo})` : ""}`);
      const itemsTotal = selectedItems.reduce((sum, s) => sum + cents(s.valor) * s.qty, 0);
      const entrega = form.modo_entrega === "retirada" ? 0 : cents(form.taxa_entrega);
      setForm(f => ({ ...f, descPedido: lines.join("\n"), Valor_Total: moneyText(itemsTotal + entrega - cents(form.desconto)) }));
    }
  },[ready, submission, selectedItems, form.taxa_entrega, form.desconto, form.modo_entrega]);

  useEffect(() => {
    if (!ready || submission.locked) return;
    setPayments(previous => previous.length === 1 ? [{ ...previous[0], valor: moneyText(cents(form.Valor_Total) + cents(form.troco)) }] : previous);
  }, [ready, submission, form.Valor_Total, form.troco]);

  const handleSave = async () => {
    if (saveGuard.current || storageError || !ready) return;
    if (!form.nomeCliente) return;
    if (form.itens?.length && !selectedItems.length) {
      alert("Mantenha pelo menos um produto no pedido.");
      return;
    }
    saveGuard.current = true;
    setSaving(true);
    try {
      const payload = { ...form };
      if (selectedItems.length) {
        payload.itens = cartLines(selectedItems);
        payload.pagamentos = payments;
        payload.pagamento = payments.map(p => p.metodo).join(" + ");
        payload.desconto = form.desconto || "0";
        payload.troco = form.troco || "0";
        payload.modo_entrega = form.modo_entrega || (form.enderecoCliente === "Retirada na loja" ? "retirada" : "entrega");
        const error = cpfError(payload.cpf_nota, payload.modo_entrega);
        if (error) throw new Error(error);
      }
      let response;
      if (editingOrder) {
        const current = await api.get(`/orders/${editingOrder.id}/data`);
        if (current.data.versao !== form.versao) {
          setEditConflict(true);
          throw new Error("Pedido alterado por outra operação. Seu formulário foi preservado; confira o pedido antes de reaplicar as alterações.");
        }
        response = await updateOrder({ id: editingOrder.id, versao: form.versao }, payload);
        submission.newOrder();
      } else {
        const result = await submission.submit(payload, body => api.post("/orders", body));
        if (!result) return;
        response = result.response;
      }
      onSaved(response.data);
      onOpenChange(false);
    } catch (err) {
      if (editingOrder && [404, 409].includes(err.response?.status)) setEditConflict(true);
      const detail = err.response?.data?.detail;
      alert(Array.isArray(detail) ? detail.map(e => e.msg).join("; ") : detail || err.message || "Erro ao salvar pedido");
    } finally { saveGuard.current = false; setSubmissionState({ ...submission.record }); setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wide" style={{ fontFamily: "Oswald, sans-serif" }}>
            {editingOrder ? "Editar Pedido" : "Novo Pedido"}
          </DialogTitle>
        </DialogHeader>
        {(storageError || submission.locked) && <p role="status" className="text-sm text-primary py-2">{storageError || (submissionState.estado === "aceito" ? `Pedido ${submissionState.resultado.id} recebido. Feche e clique em Nova compra para começar outro.` : "Envio pendente. Retome os mesmos dados antes de criar outra compra.")}</p>}
        <fieldset disabled={saving || submission.locked || !!storageError} className="space-y-4 py-2 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Nome do Cliente</Label>
              <Input value={form.nomeCliente} onChange={e => setForm({ ...form, nomeCliente: e.target.value })} className="bg-input border-transparent focus:border-primary h-10" placeholder="Nome" />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Telefone</Label>
              <Input value={form.telefoneCliente} onChange={e => setForm({ ...form, telefoneCliente: e.target.value })} className="bg-input border-transparent focus:border-primary h-10" placeholder="559899..." />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Endereco</Label>
            <Input value={form.enderecoCliente} onChange={e => setForm({ ...form, enderecoCliente: e.target.value })} className="bg-input border-transparent focus:border-primary h-10" placeholder="Endereco ou 'Retirada no local'" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Pagamento</Label>
              <Input value={form.pagamento} onChange={e => setForm({ ...form, pagamento: e.target.value })} className="bg-input border-transparent focus:border-primary h-10" placeholder="Pix, Dinheiro..." />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Taxa Entrega</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">R$</span>
                <Input value={form.taxa_entrega} onChange={e => setForm({ ...form, taxa_entrega: e.target.value })} className="bg-input border-transparent focus:border-primary h-10 pl-8" placeholder="0" />
              </div>
            </div>
            {editingOrder && (
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Status</Label>
                <Select value={form.statusPedido} onValueChange={v => setForm({ ...form, statusPedido: v })}>
                  <SelectTrigger className="bg-input border-transparent h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Separator className="bg-border" />
          {!detailsLoading && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Selecione itens do cardapio</p>
              <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-md p-2 bg-secondary/20">
                {Object.entries(menuByTipo).map(([tipo, items]) => (
                  <div key={tipo}>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 sticky top-0 bg-background/80 backdrop-blur-sm py-0.5">{TIPO_LABELS[tipo] || tipo}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                      {items.map(item => (
                        <button key={item.id} onClick={() => addSelectedItem(item)} className="text-left px-2 py-1.5 rounded text-xs hover:bg-primary/15 transition-colors border border-transparent hover:border-primary/30 truncate">
                          <span className="font-medium">{item.nome_item}</span> <span className="text-muted-foreground ml-1">R${item.valor}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {selectedItems.map(item => (
            <div key={item.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{item.nome_item}</span>
              <Input aria-label={`Quantidade de ${item.nome_item}`} type="number" min="1" max="999" className="w-20"
                value={item.qty} onChange={e => updateQty(item.id, Number(e.target.value))} />
              <Button type="button" variant="ghost" aria-label={`Remover ${item.nome_item}`} onClick={() => removeSelectedItem(item.id)}>×</Button>
            </div>
          ))}
          {!selectedItems.length && <p className="text-xs text-muted-foreground">Pedido sem itens estruturados: precisa de conferência antes da emissão fiscal.</p>}
          {selectedItems.length > 0 && <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="order-discount">Desconto (R$)</Label><Input id="order-discount" inputMode="decimal" value={form.desconto || "0"} onChange={e => setForm({ ...form, desconto: e.target.value.replace(",", ".") })} /></div>
              <div><Label htmlFor="order-cpf">{form.modo_entrega === "retirada" ? "CPF na nota (opcional)" : "CPF do destinatário (obrigatório)"}</Label><Input id="order-cpf" inputMode="numeric" maxLength={14} required={form.modo_entrega !== "retirada"} value={form.cpf_nota || ""} onChange={e => setForm({ ...form, cpf_nota: e.target.value.replace(/\D/g, "").slice(0, 11) || null })} /></div>
            </div>
            <Label htmlFor="order-delivery">Entrega ou retirada</Label>
            <Select value={form.modo_entrega || "entrega"} onValueChange={modo_entrega => setForm({ ...form, modo_entrega })}>
              <SelectTrigger id="order-delivery" className="h-10 bg-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="entrega">Entrega</SelectItem><SelectItem value="retirada">Retirada na loja</SelectItem></SelectContent>
            </Select>
            {form.modo_entrega !== "retirada" && <DeliveryFields value={form.endereco_entrega || {}} onChange={endereco_entrega => setForm({ ...form, endereco_entrega })} />}
            <PaymentFields payments={payments} onChange={setPayments} change={form.troco} onChangeChange={troco => setForm({ ...form, troco })} />
          </>}
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Descricao do Pedido</Label>
            <textarea readOnly={selectedItems.length > 0} value={form.descPedido} onChange={e => setForm({ ...form, descPedido: e.target.value })} className="w-full bg-input border-transparent focus:border-primary rounded-md p-3 text-sm text-foreground min-h-[100px] resize-y focus:outline-none focus:ring-1 focus:ring-primary" placeholder="1 - Pizza Calabresa..." />
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Valor Total</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">R$</span>
                <Input readOnly={selectedItems.length > 0} value={form.Valor_Total} onChange={e => setForm({ ...form, Valor_Total: e.target.value })} className="bg-input border-transparent focus:border-primary h-10 pl-8 w-40 text-lg font-bold text-primary" />
              </div>
            </div>
            {form.Valor_Total && <span className="text-2xl font-bold text-primary" style={{ fontFamily: "Oswald, sans-serif" }}>{fmtBRL(form.Valor_Total)}</span>}
          </div>
        </fieldset>
        <DialogFooter>
          {editConflict && <div className="space-y-2">
            <p role="alert" className="text-sm text-destructive">A edição foi preservada. Confira o pedido antes de continuar.</p>
            <Button variant="secondary" disabled={saving} onClick={async () => {
              if (!window.confirm("Descartar as alterações deste formulário e carregar a versão atual do pedido?")) return;
              setSaving(true);
              try {
                const { data } = await api.get(`/orders/${editingOrder.id}/data`);
                submission.newOrder(); loadOrder(data); setEditConflict(false);
              } catch (err) { alert(err.response?.data?.detail || "Não foi possível carregar o pedido. Sua edição foi mantida."); }
              finally { setSaving(false); }
            }}>Descartar edição e carregar versão atual</Button>
          </div>}
          {detailsError && <p role="alert" className="text-sm text-destructive">Não foi possível carregar os itens. Feche e tente novamente.</p>}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || detailsLoading || detailsError || !!storageError || !form.nomeCliente || submissionState.estado === "aceito"} className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20">
            {saving ? "Salvando..." : editingOrder ? "Salvar Alteracoes" : submission.locked ? "Retomar envio" : "Criar Pedido"}
          </Button>
          {submissionState.estado === "aceito" && <Button onClick={() => { try { submission.newOrder(); onOpenChange(false); } catch (err) { setStorageError(err.message); } }}>Nova compra</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= CONFIRM DIALOG =============
function ConfirmDialog({ open, title, description, confirmLabel = "Confirmar", variant = "destructive", onConfirm, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onCancel(); }}>
      <DialogContent className="bg-card border-border sm:max-w-sm">
        <DialogHeader className="flex flex-col items-center gap-3 pt-2">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${variant === "destructive" ? "bg-destructive/15" : "bg-yellow-900/30"}`}>
            <AlertTriangle className={`w-6 h-6 ${variant === "destructive" ? "text-destructive" : "text-yellow-500"}`} />
          </div>
          <DialogTitle className="text-center text-base">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-center text-sm text-muted-foreground">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="flex flex-row gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onCancel}>Cancelar</Button>
          <Button
            variant={variant}
            className={`flex-1 ${variant !== "destructive" ? "bg-primary hover:bg-primary/90 text-white" : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============= MAIN PAGE (VISÃO DIÁRIA) =============
export default function OrdersPage() {
  const [fiscalOrder, setFiscalOrder] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirm, setConfirm] = useState({ open: false, title: "", description: "", onConfirm: null });
  const printingOrders = useRef(new Set());

  const openConfirm = ({ title, description, onConfirm }) =>
    setConfirm({ open: true, title, description, onConfirm });
  const closeConfirm = () => setConfirm(s => ({ ...s, open: false, onConfirm: null }));

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const displayDate = format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR });

  // Summary derivado diretamente do estado de orders — sem re-fetch
  const summary = useMemo(() => ({
    grand_count: orders.length,
    grand_total: orders.reduce((acc, o) => acc + (parseFloat(o.Valor_Total) || 0), 0),
  }), [orders]);

  const pendingCount = useMemo(
    () => orders.filter(o => ['pendente', 'preparando'].includes(o.statusPedido)).length,
    [orders]
  );

  const openNewOrder = () => { setEditingOrder(null); setDialogOpen(true); };
  const openEditOrder = (order) => { setEditingOrder(order); setDialogOpen(true); };

  const upsertOrder = useCallback((order) => {
    if (!order?.id || !order.created_at) return;

    const orderDate = format(new Date(order.created_at), "yyyy-MM-dd");
    setOrders(current => {
      const previous = current.find(item => item.id === order.id);
      const updated = newestOrder(previous, order);
      if (updated === previous) return current;
      const withoutOrder = current.filter(item => item.id !== order.id);
      if (orderDate !== todayStr) return withoutOrder;
      return [updated, ...withoutOrder].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    });
  }, [todayStr]);

  const handleImprimir = async (order) => {
    if (printingOrders.current.has(order.id)) return;
    printingOrders.current.add(order.id);
    let dialogOpened = false;
    try {
      const response = await api.get(`/orders/${order.id}/print`);
      const htmlComanda = response.data.html_comanda;
      await printReceipt(htmlComanda);
      dialogOpened = true;
      if (response.data.Impresso !== "true" && response.data.Impresso !== true) {
        const printed = await updateOrder(response.data, { Impresso: "true" });
        upsertOrder(printed.data);
      }
    } catch (err) {
      console.error("Erro ao buscar comanda para impressao", err);
      alert(dialogOpened ? "A impressão foi aberta, mas não foi possível registrar o status. Confira o pedido antes de imprimir novamente." :
        "Não foi possível abrir a comanda para impressão. Tente novamente.");
    } finally {
      printingOrders.current.delete(order.id);
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get("/orders/recent", {
        params: { date: todayStr, limit: 100 }
      });
      setOrders(response.data);
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [todayStr]);

  useEffect(() => {
    fetchData(false);
    const handlePedidoAtualizado = event => upsertOrder(event.detail?.pedido);
    window.addEventListener("pedidoAtualizado", handlePedidoAtualizado);
    return () => window.removeEventListener("pedidoAtualizado", handlePedidoAtualizado);
  }, [fetchData, upsertOrder]);

  // ---- Delete único (otimista) ----
  const handleDelete = (id) => {
    setDeleteError("");
    openConfirm({
      title: "Remover pedido?",
      description: "Esta ação não pode ser desfeita.",
      onConfirm: async () => {
        closeConfirm();
        const snapshot = orders;
        setOrders(prev => prev.filter(o => o.id !== id));
        setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
        try {
          await api.delete(`/orders/${id}`);
        } catch (error) {
          setOrders(snapshot);
          setDeleteError(fiscalError(error));
        }
      },
    });
  };

  // ---- Delete em massa (otimista) ----
  const handleBulkDelete = () => {
    setDeleteError("");
    openConfirm({
      title: `Remover ${selectedIds.size} pedido(s)?`,
      description: "Todos os pedidos selecionados serão excluídos permanentemente.",
      onConfirm: async () => {
        closeConfirm();
        const ids = [...selectedIds];
        const snapshot = orders;
        setOrders(prev => prev.filter(o => !selectedIds.has(o.id)));
        setSelectedIds(new Set());
        const results = await Promise.allSettled(ids.map(id => api.delete(`/orders/${id}`)));
        const failure = results.find(result => result.status === "rejected");
        if (failure) {
          const failedIds = new Set(ids.filter((id, i) => results[i].status === "rejected"));
          setOrders(prev => [...prev.filter(order => !failedIds.has(order.id)), ...snapshot.filter(order => failedIds.has(order.id))]);
          setDeleteError(fiscalError(failure.reason));
        }
      },
    });
  };

  // ---- Mudança de status em massa (otimista) ----
  const handleBulkStatusChange = async (newStatus) => {
    const ids = [...selectedIds];
    const snapshot = orders;
    setOrders(prev => prev.map(o => selectedIds.has(o.id) ? { ...o, statusPedido: newStatus } : o));
    setSelectedIds(new Set());
    const results = await Promise.allSettled(ids.map(async id => updateOrder(snapshot.find(o => o.id === id), { statusPedido: newStatus })));
    const accepted = results.filter(r => r.status === "fulfilled").map(r => r.value.data);
    accepted.forEach(upsertOrder);
    if (results.some(r => r.status === "rejected")) {
      await fetchData(true);
      alert("Alguns pedidos não foram atualizados. A lista foi recarregada para conferir as alterações.");
    }
  };

  // ---- Status único (otimista) ----
  const handleStatusChange = async (id, newStatus) => {
    const snapshot = orders;
    setOrders(prev => prev.map(o => o.id === id ? { ...o, statusPedido: newStatus } : o));
    try {
      const response = await updateOrder(snapshot.find(o => o.id === id), { statusPedido: newStatus });
      upsertOrder(response.data);
    } catch (err) {
      await fetchData(true);
      alert(err.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  // ---- Seleção ----
  const allSelected = orders.length > 0 && selectedIds.size === orders.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up stagger-1">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: "Oswald, sans-serif" }}>
            Pedidos de Hoje
          </h1>
          <p className="text-muted-foreground mt-1 capitalize">{displayDate}</p>
        </div>
        <Button onClick={openNewOrder} className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 gap-2">
          <Plus className="w-4 h-4" /> Novo Pedido
        </Button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-in-up stagger-2">
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-primary/15 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Hoje</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "Oswald, sans-serif" }}>{summary.grand_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Faturamento Hoje</p>
              <p className="text-2xl font-bold text-green-400" style={{ fontFamily: "Oswald, sans-serif" }}>{fmtBRL(summary.grand_total)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-shadow">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-md bg-yellow-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Em Preparo / Pendentes</p>
              <p className="text-2xl font-bold text-yellow-500" style={{ fontFamily: "Oswald, sans-serif" }}>{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator className="bg-border" />

      {/* Barra de ações em massa */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/10 border border-primary/20 rounded-lg animate-fade-in-up">
          <span className="text-sm font-medium text-primary">{selectedIds.size} selecionado(s)</span>
          <div className="flex items-center gap-2 ml-auto">
            <Select onValueChange={handleBulkStatusChange}>
              <SelectTrigger className="h-8 w-44 bg-card border-border text-xs">
                <SelectValue placeholder="Alterar status..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 gap-1.5 text-xs"
              onClick={handleBulkDelete}
            >
              <Trash2 className="w-3.5 h-3.5" /> Excluir
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-muted-foreground"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-card border border-border rounded-lg animate-pulse" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground animate-fade-in-up stagger-3 bg-card border border-border rounded-lg">
          <ReceiptText className="w-14 h-14 mx-auto mb-4 opacity-20" />
          <p className="text-lg">Nenhum pedido recebido hoje ainda.</p>
          <p className="text-sm mt-1">Os pedidos do n8n aparecerão aqui automaticamente.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden card-shadow animate-fade-in-up stagger-3">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onCheckedChange={toggleAll}
                    className="border-muted-foreground/40"
                  />
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Hora</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliente</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Descricao</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Pgto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Entrega</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:table-cell">Endereço</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-center">Impresso</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map(order => {
                const horaPedido = order.created_at
                  ? new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                  : "-";
                const isSelected = selectedIds.has(order.id);

                return (
                  <TableRow
                    key={order.id}
                    className={`border-b border-border transition-colors group ${isSelected ? "bg-primary/5 hover:bg-primary/8" : "hover:bg-white/[0.02]"}`}
                  >
                    <TableCell className="pl-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(order.id)}
                        className="border-muted-foreground/40"
                      />
                    </TableCell>
                    <TableCell className="text-xs font-medium text-foreground">{horaPedido}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium truncate max-w-[120px]">{order.nomeCliente || "-"}</p>
                      {order.telefoneCliente && <p className="text-[10px] text-muted-foreground">{(order.telefoneCliente || "").replace("@s.whatsapp.net", "")}</p>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="text-xs text-muted-foreground truncate max-w-[200px] whitespace-pre-line leading-tight">{order.descPedido || "-"}</p>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden sm:table-cell truncate max-w-[100px]">{order.pagamento || "-"}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="text-xs text-muted-foreground truncate max-w-[200px] whitespace-pre-line leading-tight">{order.enderecoCliente || "-"}</p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {order.taxa_entrega && order.taxa_entrega !== "0" ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Truck className="w-3 h-3" /> R${order.taxa_entrega}</div>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={order.statusPedido || ""} onValueChange={v => handleStatusChange(order.id, v)}>
                        <SelectTrigger className="h-7 w-28 border-none bg-transparent p-0 text-xs focus:ring-0">
                          <Badge className={STATUS_MAP[order.statusPedido]?.cls || "bg-secondary border-transparent"}>
                            {STATUS_MAP[order.statusPedido]?.label || order.statusPedido || "-"}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      {order.Impresso === "true" || order.Impresso === true ? (
                        <Badge className="bg-emerald-900/50 text-emerald-100 border-transparent gap-1 font-normal">
                          <Printer className="w-3 h-3" /> Sim
                        </Badge>
                      ) : (
                        <Badge className="bg-secondary text-muted-foreground border-transparent gap-1 font-normal">
                          <Printer className="w-3 h-3" /> Não
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right"><span className="text-sm font-bold text-accent">{fmtBRL(order.Valor_Total)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {isFiscalAdmin() && <Button variant="outline" size="sm" className="h-8" onClick={() => setFiscalOrder(order)}>NFC-e</Button>}
                        <Button title="Imprimir Comanda" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => handleImprimir(order)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEditOrder(order)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(order.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {dialogOpen && <OrderDialog key={editingOrder?.pedido_uid || editingOrder?.id || "nova"} open={dialogOpen} onOpenChange={setDialogOpen} onSaved={upsertOrder} editingOrder={editingOrder} />}

      {deleteError && <p role="alert" className="text-sm text-destructive mt-4">{deleteError}</p>}
      {fiscalOrder && <FiscalOrderDialog key={fiscalOrder.id} order={fiscalOrder} onClose={() => setFiscalOrder(null)} />}
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmLabel="Excluir"
        variant="destructive"
        onConfirm={() => confirm.onConfirm?.()}
        onCancel={closeConfirm}
      />
    </div>
  );
}
